/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { SettingContext } from '@/data/context/setting';
import { useContext, useMemo } from 'react';
import useSWR from 'swr';

// some stepwise refinement
import {
	constructNextUrl,
	dAdd,
	dDiv,
	dFix,
	dMul,
	extractParamsOfConcern,
	getActiveOrganizationId,
	getAsPath,
	getContractIdParamFromContext,
	getSettings,
	isB2BStore,
} from '@/data/Settings-Server';
import { Settings as SettingsType, fetcher } from '@/data/_Settings';
import { DATA_KEY_SETTINGS } from '@/data/constants/dataKey';
import { transactionsStore } from 'integration/generated/transactions';
import { StoreStoreItem } from 'integration/generated/transactions/data-contracts';
import { usePathname, useSearchParams } from 'next/navigation';
import { STORE_ID } from './config/STORE_ID';
export {
	constructNextUrl,
	dAdd,
	dDiv,
	dFix,
	dMul,
	extractParamsOfConcern,
	getActiveOrganizationId,
	getAsPath,
	getContractIdParamFromContext,
	getSettings,
	isB2BStore,
};
export type Settings = SettingsType;
const currencySymbols: Record<string, string> = {
	USD: '$',
	MXN: '$',
};

const DATA_KEY = DATA_KEY_SETTINGS;
const dataMap = ({
	supportedCurrencies,
	supportedLanguages,
	storeId,
	description,
	identifier,
	...rest
}: StoreStoreItem): Omit<Omit<Settings, 'inventorySystem'>, 'defaultCatalogId'> => ({
	supportedCurrencies: supportedCurrencies?.supportedCurrencies || [],
	defaultCurrency: supportedCurrencies?.defaultCurrency || '',
	supportedLanguages: supportedLanguages?.supportedLanguages || [],
	defaultLanguage: supportedLanguages?.defaultLanguageId || '',
	currencySymbol: currencySymbols[supportedCurrencies?.defaultCurrency || ''],
	storeId: storeId ?? '',
	storeName: (description?.at(0)?.displayName ?? identifier) as string,
	identifier,
	...rest,
});

export const useSettings = () => {
	const settings = useContext(SettingContext);
	return {
		settings,
	};
};
const extractStoreItemFirstResult = (data: { resultList?: StoreStoreItem[] }) =>
	data?.resultList?.at(0) || {};

const fetchOnlineStoreSingleData = async (pub: boolean, storeId: string) =>
	dataMap(
		extractStoreItemFirstResult(
			await transactionsStore(pub).storeFindByQueryOnlineStore(storeId, {} as any)
		)
	);

export const useStaticSettings = () => {
	const path = usePathname();
	const params = useSearchParams();
	const asPath = useMemo(() => getAsPath(path, params), [path, params]);
	// static generated error pages does not have query, need to use asPath.
	const {
		queryOfConcern: { storeId = STORE_ID, storeIdentifier },
		storeTokenCandidate,
	} = useMemo(() => extractParamsOfConcern(asPath), [asPath]);

	const props = storeId
		? {
				storeId,
				storeTokenCandidate,
		  }
		: storeIdentifier
		? {
				storeIdentifier,
				storeTokenCandidate,
		  }
		: { storeTokenCandidate };
	const { data, error } = useSWR([props, DATA_KEY], async ([props]) => fetcher(true)(props));
	return {
		settings: data,
		loading: !error && !data,
		error,
	};
};
export const useSingleSettings = () => {
	const {
		settings: { storeId },
	} = useSettings();
	const props = {
		storeId,
	};

	const { data, error } = useSWR([props, DATA_KEY], async ([props]) =>
		fetchOnlineStoreSingleData(true, props?.storeId)
	);

	return {
		settings: data,
		loading: !error && !data,
		error,
	};
};
